import React, { useEffect, useRef, useState } from 'react'
import {
	CheckIcon,
	ChevronUpDownIcon,
	ClockIcon,
	MapPinIcon,
} from '@heroicons/react/24/outline'
import useGeocodeSuggest from './useGeocodeSuggest'
import { Label } from './FilterFields'

const MAX_RECENT_CITIES = 10

const readRecents = key => {
	if (!key) return []
	try {
		const raw = window.localStorage.getItem(key)
		const arr = raw ? JSON.parse(raw) : []
		return Array.isArray(arr) ? arr : []
	} catch {
		return []
	}
}

const writeRecents = (key, list) => {
	if (!key) return
	try {
		window.localStorage.setItem(key, JSON.stringify(list.slice(0, MAX_RECENT_CITIES)))
	} catch {
		// localStorage can throw in private modes; recents are non-essential.
	}
}

function LocationField({
	value,
	onChange,
	placeholder,
	required,
	label,
	tokens,
	inputBase,
	flashKey = 0,
	storageKey,
	radiusValue,
	onRadiusChange,
	radiusOptions,
}) {
	const [isOpen, setIsOpen] = useState(false)
	const [flashing, setFlashing] = useState(false)
	const [recents, setRecents] = useState(() => readRecents(storageKey))
	
	const [draft, setDraft] = useState(null)
	const wrapRef = useRef(null)
	const inputRef = useRef(null)

	const isEditing = draft !== null
	const displayValue = isEditing ? draft : value
	const hasQuery = isEditing && draft.trim().length > 0
	const { suggestions, loading } = useGeocodeSuggest(draft || '', {
		enabled: isOpen && hasQuery,
	})

	useEffect(() => {
		if (!flashKey) return
		setFlashing(true)
		const t = setTimeout(() => setFlashing(false), 1500)
		return () => clearTimeout(t)
	}, [flashKey])

	useEffect(() => {
		const handleClickOutside = e => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				setIsOpen(false)
				setDraft(null)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const pushRecent = entry => {
		if (!storageKey) return
		setRecents(prev => {
			const dedup = prev.filter(r => r.label !== entry.label)
			const next = [entry, ...dedup].slice(0, MAX_RECENT_CITIES)
			writeRecents(storageKey, next)
			return next
		})
	}

	const commit = (label, lat, lng) => {
		onChange(label, { lat, lng })
		pushRecent({ label, lat, lng })
		setDraft(null)
		setIsOpen(false)
		inputRef.current?.blur()
	}

	const select = s =>
		commit(`${s.city}, ${s.stateCode}`, Number(s.lat), Number(s.lon))

	const selectRecent = r => commit(r.label, r.lat, r.lng)

	const deselectCurrent = () => {
		onChange('', null)
	}

	const currentLabel = (value || '').trim()
	const recentsToShow = recents.filter(r => r.label !== currentLabel)
	const showRecents =
		isOpen && isEditing && !hasQuery && (currentLabel || recentsToShow.length > 0)
	const showSuggestions = isOpen && hasQuery && (suggestions.length > 0 || loading)

	return (
		<div
			className={`flex-1 min-w-[80px] max-w-[500px] relative rounded-md transition-shadow ${
				flashing ? 'ring-2 ring-rose-500 animate-pulse' : ''
			}`}
			ref={wrapRef}
		>
			<div className={tokens.fieldShell}>
				<Label required={required}>{label}</Label>
				<div className="flex items-center gap-2">
					<MapPinIcon className={`h-4 w-4 shrink-0 ${tokens.valueMuted}`} strokeWidth={2} />
					<input
						ref={inputRef}
						type="text"
						value={displayValue}
						onChange={e => {
							setDraft(e.target.value)
							setIsOpen(true)
						}}
						onFocus={() => {
							setDraft('')
							setIsOpen(true)
						}}
						placeholder={placeholder}
						className={`${inputBase} min-w-0 flex-1`}
						autoComplete="off"
					/>
					{radiusValue != null && onRadiusChange && Array.isArray(radiusOptions) && (
						<div className={`relative shrink-0 ${!value ? 'opacity-40 pointer-events-none' : ''}`}>
							<select
								value={radiusValue}
								onChange={e => onRadiusChange(Number(e.target.value))}
								onMouseDown={e => e.stopPropagation()}
								disabled={!value}
								className={`appearance-none bg-transparent text-[12.5px] font-mono pr-5 cursor-pointer focus:outline-none disabled:cursor-not-allowed ${tokens.valueMuted}`}
								aria-label="Radius (miles)"
							>
								{radiusOptions.map(n => (
									<option key={n} value={n} className={tokens.optionBg}>
										{n}mi
									</option>
								))}
							</select>
							<ChevronUpDownIcon
								className={`absolute right-0 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none ${tokens.valueMuted}`}
							/>
						</div>
					)}
				</div>
			</div>
			{(showRecents || showSuggestions) && (
				<div
					className={`absolute left-0 right-0 top-full mt-1 z-30 rounded-md max-h-72 overflow-auto ${tokens.dropdownBg}`}
				>
					{showSuggestions && loading && suggestions.length === 0 && (
						<div className={`px-3 py-2 text-xs ${tokens.valueMuted}`}>
							Searching…
						</div>
					)}
					{showSuggestions &&
						suggestions.map(s => (
							<button
								key={s.id}
								type="button"
								onMouseDown={e => e.preventDefault()}
								onClick={() => select(s)}
								className={`w-full text-left px-3 py-2 text-sm ${tokens.dropdownItem}`}
							>
								<div className="font-medium">
									{s.city}, {s.stateCode}
								</div>
							</button>
						))}
					{showRecents && currentLabel && (
						<button
							type="button"
							onMouseDown={e => e.preventDefault()}
							onClick={deselectCurrent}
							className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${tokens.dropdownItem}`}
						>
							<CheckIcon className="h-3.5 w-3.5 text-sky-500" />
							<span className="font-semibold">{currentLabel}</span>
						</button>
					)}
					{showRecents &&
						recentsToShow.map(r => (
							<button
								key={r.label}
								type="button"
								onMouseDown={e => e.preventDefault()}
								onClick={() => selectRecent(r)}
								className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${tokens.dropdownItem}`}
							>
								<ClockIcon className={`h-3.5 w-3.5 ${tokens.valueMuted}`} />
								<span className="font-medium">{r.label}</span>
							</button>
						))}
				</div>
			)}
		</div>
	)
}

export default LocationField
